/**
 * Shared MUI v4 Button overrides for App.js (main theme) and theme.js (nested ThemeProviders).
 *
 * Hover feedback uses box-shadow (slight “lift”) rather than only darkening the fill because:
 * - Many contained buttons set `style={{ backgroundColor: ... }}` for brand/disabled states.
 *   Inline background wins over stylesheet rules, so `:hover { backgroundColor: ... }` often
 *   does nothing visible.
 * - `box-shadow` is not overridden by inline `backgroundColor`, so hover still reads clearly.
 *
 * `containedPrimary` / `containedSecondary` keep the resting fill (`main`) on hover so MUI’s
 * default `palette.*.dark` hover does not fight those cases; shadow carries the hover state.
 */

const CONTAINED_SHADOW =
  '0 1px 2px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.08)';
const CONTAINED_HOVER_SHADOW =
  '0 4px 12px rgba(15, 23, 42, 0.1), 0 2px 4px rgba(15, 23, 42, 0.06)';
const CONTAINED_ACTIVE_SHADOW = '0 1px 3px rgba(15, 23, 42, 0.08)';

const sharedContained = {
  fontWeight: 600,
  boxShadow: CONTAINED_SHADOW,
  '&:hover': {
    boxShadow: CONTAINED_HOVER_SHADOW,
  },
  '&:active': {
    boxShadow: CONTAINED_ACTIVE_SHADOW,
  },
};

/**
 * @param {import("@material-ui/core").Theme} theme
 */
export function getMuiButtonOverrides(theme) {
  return {
    root: {
      textTransform: 'none',
      borderRadius: 8,
      letterSpacing: '0.01em',
      fontWeight: 500,
      transition:
        'background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, color 0.2s ease',
    },
    contained: sharedContained,
    containedPrimary: {
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        boxShadow: CONTAINED_HOVER_SHADOW,
        '@media (hover: none)': {
          backgroundColor: theme.palette.primary.main,
        },
      },
    },
    containedSecondary: {
      '&:hover': {
        backgroundColor: theme.palette.secondary.main,
        boxShadow: CONTAINED_HOVER_SHADOW,
        '@media (hover: none)': {
          backgroundColor: theme.palette.secondary.main,
        },
      },
    },
    outlined: {
      fontWeight: 600,
    },
    text: {
      fontWeight: 500,
    },
  };
}
